import { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from '../../../components/Slots/SlotsComponentes';
import { Button } from '../../../ui/Button';
import ColorPicker from '../../../ui/ColorPicker/ColorPicker';
import Input from '../../../ui/Input';
import Slider from '../../../ui/Slider/Slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/Select/Select';
import InputNumber from '../../../ui/InputNumber';
import {
	classAttr,
	elementAt,
	listasDoBloco,
	origemDoValor,
	papelDe,
	selDoTexto,
	type CampoDado,
	type JsxElement,
	type Papel,
} from '../code/parse';
import { ehEmbutida, pesoKB } from '../code/imagem';
import { setAttr, setCampo, setValorNaOrigem } from '../code/edit';
import { CLASS_GROUPS, colorToken, getClass, setClass, tokenToColor, type ClassGroup } from '../code/classes';
import { actions, useStore } from './store';

const SUPERFICIE = ['bg', 'radius', 'borderWidth', 'borderColor', 'shadow', 'padding'];

const PAPEIS: Record<Papel, { titulo: string; grupos: string[]; texto?: boolean; foto?: boolean }> = {
	raiz: { titulo: 'Container', grupos: SUPERFICIE },
	superficie: { titulo: 'Superfície', grupos: SUPERFICIE },
	botao: {
		titulo: 'Botão',
		grupos: ['bg', 'color', 'fontSize', 'weight', 'radius', 'padding', 'borderWidth', 'borderColor', 'shadow'],
		texto: true,
	},
	imagem: { titulo: 'Imagem', grupos: ['radius', 'borderWidth', 'borderColor'], foto: true },
	texto: { titulo: 'Texto', grupos: ['color', 'fontSize', 'weight', 'textAlign', 'bg', 'radius', 'padding'], texto: true },
};

function useAdiado<T>(doCodigo: T, escreve: (v: T) => void, ms = 400) {
	const [local, setLocal] = useState(doCodigo);
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const pendente = useRef<(() => void) | null>(null);

	const solta = () => {
		const escrever = pendente.current;
		if (!escrever) return;
		pendente.current = null;
		clearTimeout(timer.current);
		escrever();
	};

	useEffect(() => {
		if (!pendente.current) setLocal(doCodigo);
	}, [doCodigo]);

	// trocar de elemento no meio da digitação não pode comer a alteração
	useEffect(
		() => () => {
			clearTimeout(timer.current);
			pendente.current?.();
		},
		[],
	);

	function muda(v: T) {
		setLocal(v);
		const escrever = escreve; // o escritor DESTE render, com o alvo certo
		pendente.current = () => escrever(v);
		clearTimeout(timer.current);
		timer.current = setTimeout(solta, ms);
	}

	return { valor: local, muda, agora: solta };
}

function EntradaTexto({ valor, escreve }: { valor: string; escreve: (v: string) => void }) {
	const campo = useAdiado(valor, escreve);
	return (
		<Input
			type="text"
			value={campo.valor}
			onBlur={campo.agora}
			onChange={(e: React.ChangeEvent<HTMLInputElement>) => campo.muda(e.target.value)}
		/>
	);
}

function EntradaNumero({ valor, escreve }: { valor: number; escreve: (v: number) => void }) {
	const campo = useAdiado(valor, escreve);
	return <InputNumber value={campo.valor} onChange={(n: number) => campo.muda(n)} />;
}

/** Arrastar o slider é contínuo como o seletor de cor: escreve quando parar. */
function EntradaEscala({ index, options, escreve }: { index: number; options: string[]; escreve: (token: string) => void }) {
	const campo = useAdiado(index, (i) => escreve(options[i]), 250);
	return (
		<Slider
			value={campo.valor}
			min={0}
			max={Math.max(1, options.length - 1)}
			step={1}
			onChange={(_: unknown, val: number | number[]) => campo.muda(Array.isArray(val) ? val[0] : val)}
		/>
	);
}

/** 250ms: arrastar o seletor é contínuo, e 400 faria a cor parecer presa. */
function EntradaCor({ valor, rotulo, escreve }: { valor: string; rotulo: string; escreve: (v: string) => void }) {
	const campo = useAdiado(valor, escreve, 250);
	return (
		<ColorPicker align="start" side="bottom" sideOffset={20} color={campo.valor} placeholder={rotulo} onChange={campo.muda} />
	);
}

/** O texto de ajuda do cabeçalho — quem monta o Heading é o painel que hospeda. */
export function dicaDaSelecao(source: string, selected: string | null): string {
	if (!source) return 'Descreva o componente para começar.';
	// o Alt+clique não se descobre sozinho, e é o único lugar que sempre aparece
	if (!selected) return 'Clique num elemento do bloco. Alt+clique pega o elemento exato.';
	return 'Edite o elemento selecionado.';
}

/** Só os Containers: o ScrollArea e o Heading são do painel que hospeda. */
export function Inspector() {
	const source = useStore((s) => s.source);
	const selected = useStore((s) => s.selected);

	const alvo = useMemo(() => {
		if (!source || !selected) return null;
		try {
			const el = elementAt(source, selected);
			if (!el) return null;
			const papel = papelDe(source, selected);
			return { papel, el, painel: papel ? PAPEIS[papel] : null };
		} catch {
			return null;
		}
	}, [source, selected]);

	if (!alvo) return <Dados source={source} />;

	const { papel, el, painel } = alvo;
	// o modelo escreve className ou class; escreve de volta no mesmo nome que achou
	const classe = classAttr(el);
	const className = classe?.value ?? '';
	const attr = classe?.name ?? 'className';

	const grupos = (painel?.grupos ?? []).map((id) => CLASS_GROUPS.find((g) => g.id === id)).filter(Boolean) as ClassGroup[];
	const cores = grupos.filter((g) => g.kind === 'color');
	const demais = grupos.filter((g) => g.kind !== 'color');

	const calculado = !!classe && classe.value === null;

	return (
		<>
			{painel && (painel.texto || painel.foto) && (
				<Container id="bloco-conteudo">
					<Container.Trigger title="Conteúdo" />

					<Container.Slot>
						{painel.texto && <CampoTexto source={source} sel={selected!} />}
						{painel.foto && <CampoFoto source={source} sel={selected!} />}
					</Container.Slot>
				</Container>
			)}

			{painel && (
				<Container id="bloco-estilos" opened>
					{/* o título diz o que está selecionado — é a única pista fora da seção Remover */}
					<Container.Trigger title={painel.titulo} />

					<Container.Slot>
						{calculado ? (
							<Container.Help>Esse elemento monta as classes em tempo real — só dá para mudar pelo prompt.</Container.Help>
						) : (
							demais.flatMap((group) => [
								<Container.Label key={`${group.id}-rotulo`} placeholder={getClass(className, group.id) ?? '—'}>
									{group.label}
								</Container.Label>,
								<Control key={group.id} group={group} className={className} attr={attr} el={el} />,
							])
						)}

						{!calculado && cores.length > 0 && (
							<div className="flex flex-col gap-3">
								{cores.map((group) => (
									<Control key={group.id} group={group} className={className} attr={attr} el={el} />
								))}
							</div>
						)}
					</Container.Slot>
				</Container>
			)}

			{!painel && (
				<Container id="bloco-sem-estilo">
					<Container.Trigger title={`<${el.name}>`} />

					<Container.Slot>
						<Container.Help>Esse elemento não tem estilo para editar — dá para removê-lo.</Container.Help>
					</Container.Slot>
				</Container>
			)}

			<Remocao source={source} sel={selected!} el={el} raiz={papel === 'raiz'} />

			<Dados source={source} />
		</>
	);
}

function Remocao({ source, sel, el, raiz }: { source: string; sel: string; el: JsxElement; raiz: boolean }) {
	// tirar a raiz é apagar o bloco, e o preview ficaria sem nada para montar
	if (raiz) return null;

	// o loc só ganha "#índice" dentro de um map: um elemento no código, N na tela
	const repetido = sel.includes('#');
	// sai com a árvore inteira: quem clicou num wrapper precisa saber disso ANTES
	const embrulha = !!el.inner && source.slice(el.inner.start, el.inner.end).includes('<');

	return (
		<Container id="bloco-remover">
			<Container.Trigger title="Remover" />

			<Container.Slot>
				<Container.Label placeholder={`<${el.name}>`}>Elemento selecionado</Container.Label>

				<Button
					variant="destructive"
					onClick={() => {
						if (actions.remove(sel)) return;
						actions.setError('esse elemento não sai sozinho: ele é o único conteúdo de uma repetição, de uma condição ou do return');
					}}
				>
					Remover do bloco
				</Button>

				{embrulha && <Container.Help>Ele embrulha outros elementos — sai com tudo que está dentro.</Container.Help>}

				{repetido && <Container.Help>Ele é repetido pelo bloco — remover tira todas as cópias.</Container.Help>}
			</Container.Slot>
		</Container>
	);
}

function CampoTexto({ source, sel }: { source: string; sel: string }) {
	const alvo = useMemo(() => {
		try {
			const dono = selDoTexto(source, sel);
			return dono ? { dono, origem: origemDoValor(source, dono, null) } : null;
		} catch {
			return null;
		}
	}, [source, sel]);

	if (!alvo) return null;
	if (!alvo.origem) return <Container.Help>O texto é calculado pelo bloco — só dá para mudar pelo prompt.</Container.Help>;

	return (
		<>
			<Container.Label placeholder={alvo.origem.compartilhado ? 'vale para todas as cópias' : undefined}>Texto</Container.Label>

			<EntradaTexto
				valor={alvo.origem.valor}
				escreve={(v) => actions.apply((s) => setValorNaOrigem(s, alvo.dono, null, v))}
			/>
		</>
	);
}

function CampoFoto({ source, sel }: { source: string; sel: string }) {
	const origem = useMemo(() => {
		try {
			return origemDoValor(source, sel, 'src');
		} catch {
			return null;
		}
	}, [source, sel]);

	if (!origem) return <Container.Help>A imagem é calculada pelo bloco — só dá para trocar pelo prompt.</Container.Help>;

	return (
		<>
			<Container.Label placeholder={ehEmbutida(origem.valor) ? `embutida · ${pesoKB(origem.valor)}KB` : undefined}>
				Imagem
			</Container.Label>

			<TrocaFoto valor={origem.valor} onTroca={(dataUrl) => actions.apply((s) => setValorNaOrigem(s, sel, 'src', dataUrl))} />
		</>
	);
}

function Dados({ source }: { source: string }) {
	const listas = useMemo(() => {
		try {
			return listasDoBloco(source);
		} catch {
			return [];
		}
	}, [source]);

	if (!listas.length) return null;

	return (
		<>
			{listas.flatMap((lista) =>
				lista.itens.map((item, i) => (
					<Container key={`${lista.nome}-${i}`} id={`dados-${lista.nome}-${i}`}>
						<Container.Trigger title={item.rotulo} />

						<Container.Slot>
							{item.campos.flatMap((campo) => [
								<Container.Label key={`${campo.chave}-rotulo`} placeholder={campo.tipo === 'vazio' ? 'sem valor' : undefined}>
									{campo.chave}
								</Container.Label>,
								<CampoLista key={campo.chave} campo={campo} />,
							])}
						</Container.Slot>
					</Container>
				)),
			)}
		</>
	);
}

function CampoLista({ campo }: { campo: CampoDado }) {
	const escreve = (valor: string) => actions.apply((s) => setCampo(s, campo, valor));

	if (campo.tipo === 'cor') return <EntradaCor valor={campo.valor} rotulo={campo.chave} escreve={escreve} />;

	if (campo.tipo === 'numero') {
		return <EntradaNumero valor={Number(campo.valor)} escreve={(n) => escreve(String(n))} />;
	}

	if (campo.tipo === 'foto') return <TrocaFoto valor={campo.valor} onTroca={escreve} />;

	if (campo.tipo === 'booleano') {
		return (
			<Select value={campo.valor} onValueChange={escreve}>
				<SelectTrigger>
					<SelectValue />
				</SelectTrigger>

				<SelectContent>
					<SelectItem value="true">sim</SelectItem>
					<SelectItem value="false">não</SelectItem>
				</SelectContent>
			</Select>
		);
	}

	// texto, e `vazio` (null) que vira texto assim que alguém escrever
	return <EntradaTexto valor={campo.valor} escreve={escreve} />;
}

function TrocaFoto({ valor, onTroca }: { valor: string; onTroca: (url: string) => void }) {
	const input = useRef<HTMLInputElement>(null);
	const [enviando, setEnviando] = useState(false);
	const enviarImagem = useStore((s) => s.enviarImagem);

	async function escolher(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		e.target.value = ''; // escolher o mesmo arquivo de novo tem que disparar
		if (!file || !enviarImagem) return;

		setEnviando(true);
		// o envio é do app: se ele estourar, o `enviando` não pode ficar preso
		const url = await enviarImagem(file).catch(() => null);
		setEnviando(false);

		if (url) onTroca(url);
		else actions.setError('não consegui enviar essa imagem');
	}

	return (
		<span className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-1.5">
			<img src={valor} alt="" className="h-10 w-14 shrink-0 rounded object-cover" />

			{/* sem o app dizer como envia arquivo, o botão não teria o que fazer */}
			{enviarImagem && (
				<button
					type="button"
					disabled={enviando}
					onClick={() => input.current?.click()}
					className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60"
				>
					{enviando ? 'Enviando…' : 'Trocar'}
				</button>
			)}

			{!ehEmbutida(valor) && <span className="truncate text-xs text-gray-500">{valor}</span>}

			<input ref={input} type="file" accept="image/*" className="hidden" onChange={escolher} />
		</span>
	);
}

/** Só o controle: o rótulo é `Container.Label`, irmão dele dentro do Slot. */
function Control({
	group,
	className,
	attr,
	el,
}: {
	group: ClassGroup;
	className: string;
	/** "className" ou "class", o nome que estava no código */
	attr: string;
	el: JsxElement;
}) {
	const atual = getClass(className, group.id);

	// a classe vai inteira de volta: setClass preserva a ordem do resto
	const write = (token: string | null) =>
		actions.apply((s) => setAttr(s, el.loc, attr, setClass(className, group.id, token)));

	if (group.kind === 'color') {
		return (
			<EntradaCor
				valor={tokenToColor(atual) ?? '#000000'}
				rotulo={group.label}
				escreve={(cor) => {
					const token = colorToken(group.id, cor);
					if (token) write(token);
				}}
			/>
		);
	}

	const base = group.options ?? [];
	const options = atual && !base.includes(atual) ? [atual, ...base] : base;

	if (group.kind === 'scale') {
		return <EntradaEscala index={Math.max(0, options.indexOf(atual ?? ''))} options={options} escreve={write} />;
	}

	return (
		<Select value={atual ?? ''} onValueChange={(v: string) => write(v || null)}>
			<SelectTrigger>
				<SelectValue placeholder="—" />
			</SelectTrigger>

			<SelectContent>
				{options.map((token) => (
					<SelectItem key={token} value={token}>
						{token}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}
